/**
 * The caller's own notifications — `GET /me/notifications`, migration 0087.
 *
 * The inbox half of the bell. The live stream (`events.ts`) carries only a pointer, a `notice`
 * frame naming a row id; on it, and on every reconnect, this query is invalidated and re-read
 * here, where the server applies the recipient predicate. So nothing shown came off the wire
 * unauthenticated, and a notice that arrived while the tab was closed is still here on the
 * next load — the row is the truth, the push only says "look".
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface NotificationRow {
  id: string;
  /** `work_order.assigned` · `work_order.unassigned`. */
  kind: string;
  entityType: string;
  entityId: string;
  title: string;
  body: string | null;
  actor: { id: string; name: string } | null;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationInbox {
  data: NotificationRow[];
  unread: number;
}

export const NOTIFICATIONS_KEY = ['notifications'] as const;

/**
 * `pollMs` belongs to ONE observer — the shell, which knows whether the stream is live and polls
 * faster when it is not. Other readers (the bell) pass nothing and ride the same cache, so the
 * inbox is not fetched twice per interval.
 */
export function useNotifications(options: { pollMs?: number | false } = {}) {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => api.get<NotificationInbox>('/me/notifications', { limit: 30 }),
    /* The stream invalidates this on every notice and every reconnect; the interval is only the
       backstop for a stream that is down. */
    staleTime: 30_000,
    refetchInterval: options.pollMs ?? false,
  });
}

/** Mark the listed notifications read, or all of them when none are given. */
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: readonly string[]) =>
      api.post<{ unread: number }>('/me/notifications/read', ids && ids.length > 0 ? { ids } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}
